import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

const ResourceLoadingContext = createContext(null);

/**
 * Hook to access the app-specific resource loading functions.
 * @returns {{fetchAllResources: Function, processAllResources: Function}}
 */
export const useResourceLoading = () => {
  const context = useContext(ResourceLoadingContext);
  if (!context) {
    throw new Error('useResourceLoading must be used within a ResourceLoadingProvider');
  }
  return context;
};

const defaultLoaders = {
  /**
   * Fetches the raw, domain-specific data structure of all resources.
   * @returns {Promise<any>} A promise that resolves with the raw data from the API.
   */
  fetchAllResources: async () => {
    console.warn("fetchAllResources is not implemented in the app. Returning empty object.");
    return {};
  },
  /**
   * Transforms the raw resource data into a flat array of resource objects.
   * @param {any} rawData - The data returned from fetchAllResources.
   * @param {Array} metadata - The user's metadata (e.g., bookmarks).
   * @returns {Array<Object>} A flat array of processed resource objects.
   */
  processAllResources: (rawData, metadata) => {
    console.warn("processAllResources is not implemented in the app. Returning empty array.");
    return [];
  },
};

/**
 * A provider that makes app-specific resource loading functions available
 * to the core ResourceProvider. This should be configured in the app's entry point.
 */
export const ResourceLoadingProvider = ({ children, loaders }) => {
  const value = useMemo(() => ({
    ...defaultLoaders,
    ...loaders,
  }), [loaders]);

  return (
    <ResourceLoadingContext.Provider value={value}>
      {children}
    </ResourceLoadingContext.Provider>
  );
};

ResourceLoadingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  loaders: PropTypes.shape({
    fetchAllResources: PropTypes.func.isRequired,
    processAllResources: PropTypes.func.isRequired,
  }).isRequired,
};